import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  arrayUnion,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ImageList,
  ImageListItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { storage } from "../../firebase";
import { actorFrom, updateMeta } from "../../utils/auditFields";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyDoc } from "../../utils/firestorePaths";

const resolveStaffId = (staff, userId) =>
  staff?.find((s) => s.userId === userId)?.id || userId;

const formatDateTime = (value) => {
  if (!value) return "";
  const date = value.seconds ? new Date(value.seconds * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const MaintenanceWorkOrderDetail = ({ userInfo, userId, staff }) => {
  const { woId } = useParams();
  const navigate = useNavigate();
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const fileInputRef = useRef(null);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [alert, setAlert] = useState(null);

  const myStaffId = useMemo(
    () => resolveStaffId(staff, userId),
    [staff, userId]
  );

  const orderRef = useMemo(() => {
    if (!selectedProperty || !woId) return null;
    return propertyDoc(selectedClient, selectedProperty, "workOrders", woId);
  }, [selectedProperty, woId]);

  useEffect(() => {
    if (!orderRef) return undefined;
    const unsubscribe = onSnapshot(orderRef, (snap) => {
      setOrder(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    });
    return unsubscribe;
  }, [orderRef]);

  const handleClaim = async () => {
    if (!orderRef) return;
    setClaiming(true);
    try {
      const { name } = actorFrom(userInfo);
      await updateDoc(orderRef, {
        assignedTo: myStaffId,
        assignedToName: name,
        assignedTime: new Date(),
        ...updateMeta(userInfo),
      });
    } catch (err) {
      console.error("Failed to claim work order:", err);
      setAlert({ severity: "error", message: "Could not claim work order." });
    } finally {
      setClaiming(false);
    }
  };

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text || !orderRef) return;
    setSavingNote(true);
    try {
      const { name, uid } = actorFrom(userInfo);
      await updateDoc(orderRef, {
        notes: arrayUnion({ text, by: name, byUid: uid, at: new Date() }),
        ...updateMeta(userInfo),
      });
      setNoteText("");
    } catch (err) {
      console.error("Failed to add note:", err);
      setAlert({ severity: "error", message: "Could not save note." });
    } finally {
      setSavingNote(false);
    }
  };

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || !orderRef) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fileName = `${Date.now()}-${file.name}`;
        const imageRef = ref(storage, `work-order-images/${woId}/${fileName}`);
        await uploadBytes(imageRef, file);
        const url = await getDownloadURL(imageRef);
        await updateDoc(orderRef, {
          completionImages: arrayUnion({
            url,
            name: file.name,
            path: `work-order-images/${woId}/${fileName}`,
            uploadedAt: new Date(),
          }),
          ...updateMeta(userInfo),
        });
      }
    } catch (err) {
      console.error("Failed to upload image:", err);
      setAlert({ severity: "error", message: "Could not upload photo(s)." });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleMarkComplete = async () => {
    if (!orderRef) return;
    setCompleting(true);
    try {
      const { name } = actorFrom(userInfo);
      await updateDoc(orderRef, {
        markedCompleted: true,
        markedCompletedTime: new Date(),
        markedCompleteBy: myStaffId,
        markedCompleteByName: name,
        status: "completed",
        ...updateMeta(userInfo),
      });
      navigate("/work-orders");
    } catch (err) {
      console.error("Failed to mark complete:", err);
      setAlert({ severity: "error", message: "Could not mark complete." });
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: "auto" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/work-orders")}>
          Back
        </Button>
        <Typography sx={{ mt: 2 }}>Work order not found.</Typography>
      </Box>
    );
  }

  const notes = order.notes || [];
  const images = order.completionImages || [];
  const alreadySubmitted = order.markedCompleted === true || order.completed === true;
  // The room number stays hidden until the signed-in user claims the order.
  const claimedByMe =
    !!order.assignedTo &&
    (order.assignedTo === myStaffId || order.assignedTo === userId);
  const canWork = claimedByMe && !alreadySubmitted;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 720, mx: "auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/work-orders")}
        sx={{ mb: 1 }}
      >
        Back
      </Button>

      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      {/* Summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {claimedByMe ? (
            <Typography variant="h6" fontWeight={600}>
              Room {order.room || "—"}
            </Typography>
          ) : (
            <Box display="flex" alignItems="center" gap={0.5}>
              <LockOutlinedIcon color="disabled" />
              <Typography variant="h6" fontWeight={600} color="text.secondary">
                Room hidden
              </Typography>
            </Box>
          )}
          <Stack direction="row" spacing={1}>
            {order.priority && <Chip size="small" label={order.priority} />}
            {alreadySubmitted && (
              <Chip
                size="small"
                color="success"
                icon={<CheckCircleIcon />}
                label="Submitted"
              />
            )}
          </Stack>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {order.category || "General"} · Reported {formatDateTime(order.reportedTime)}
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="body1">
          {order.orderDetails || "No description provided."}
        </Typography>
        {order.pteApproved && (
          <Chip size="small" color="info" label="PTE Approved" sx={{ mt: 1.5 }} />
        )}
        {!claimedByMe && !alreadySubmitted && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />} sx={{ mb: 1.5 }}>
              Claim this work order to reveal the room number and start work.
            </Alert>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={
                claiming ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AssignmentIndIcon />
                )
              }
              onClick={handleClaim}
              disabled={claiming || !!order.assignedTo}
            >
              {order.assignedTo
                ? "Claimed by someone else"
                : claiming
                ? "Claiming…"
                : "Claim Work Order"}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Notes */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Notes
        </Typography>
        {notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No notes yet.
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {notes.map((note, idx) => (
              <Box key={idx}>
                <Typography variant="body2">{note.text}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {note.by || "Unknown"} · {formatDateTime(note.at)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
        {canWork && (
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={4}
              placeholder="Add a note…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <Button
              variant="outlined"
              onClick={handleAddNote}
              disabled={savingNote || !noteText.trim()}
            >
              Add
            </Button>
          </Box>
        )}
      </Paper>

      {/* Completion photos */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>
            Completion Photos
          </Typography>
          {canWork && (
            <IconButton
              color="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <CircularProgress size={22} /> : <PhotoCameraIcon />}
            </IconButton>
          )}
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFilesSelected}
        />
        {images.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No photos uploaded.
          </Typography>
        ) : (
          <ImageList cols={3} gap={8} sx={{ mt: 1 }}>
            {images.map((img, idx) => (
              <ImageListItem key={idx}>
                <img
                  src={img.url}
                  alt={img.name || `completion-${idx}`}
                  loading="lazy"
                  style={{ borderRadius: 4 }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </Paper>

      {/* Action */}
      {canWork && (
        <Button
          fullWidth
          variant="contained"
          color="success"
          size="large"
          startIcon={
            completing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />
          }
          onClick={handleMarkComplete}
          disabled={completing}
        >
          {completing ? "Submitting…" : "Mark as Completed"}
        </Button>
      )}
    </Box>
  );
};

export default MaintenanceWorkOrderDetail;
